<template>
  <div class="mk-page">
    <MarketplaceHeader />

    <section class="container py-4">
      <button type="button" class="btn btn-link px-0 mb-3" @click="$router.back()">
        <i class="fe fe-arrow-left mr-1"></i>Back
      </button>

      <div v-if="loading" class="text-muted">Loading…</div>
      <div v-else-if="!product" class="text-center text-muted py-5">
        <i class="fe fe-alert-circle" style="font-size: 2.5rem; opacity: .3;"></i>
        <p class="mt-2">This product is not available.</p>
        <router-link to="/marketplace">Back to the marketplace</router-link>
      </div>

      <div v-else class="row">
        <div class="col-md-6 mb-4">
          <div class="mk-gallery">
            <img v-if="images.length" :src="images[active]" :alt="product.name" />
            <i v-else class="fe fe-package"></i>
          </div>
          <div class="d-flex flex-wrap mt-2" style="gap: 8px;" v-if="images.length > 1">
            <button v-for="(img, i) in images" :key="img" type="button" class="mk-thumb" :class="{ active: i === active }"
              :aria-label="'Show image ' + (i + 1)" @click="active = i">
              <img :src="img" :alt="product.name + ' image ' + (i + 1)" />
            </button>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mk-panel">
            <div class="text-uppercase small text-muted">{{ product.category }}</div>
            <h1 class="h3 font-weight-bold mb-1">{{ product.name }}</h1>
            <div class="text-muted small mb-3">
              <span v-if="product.brand">{{ product.brand }} · </span>
              <span v-if="product.sku">SKU {{ product.sku }}</span>
            </div>

            <div class="mk-price">{{ money(product.unit_price) }} <small>/ {{ product.unit }}</small></div>
            <div class="small mt-1" :class="product.quantity_available > 0 ? 'text-success' : 'text-danger'">
              {{ product.quantity_available > 0 ? product.quantity_available + " " + product.unit + " in stock" : "Out of stock" }}
            </div>
            <div class="small text-muted" v-if="product.min_order_quantity > 1">
              Minimum order: {{ product.min_order_quantity }} {{ product.unit }}
            </div>

            <div class="d-flex align-items-end mt-4" style="gap: 10px;">
              <div>
                <label for="pd-qty" class="small font-weight-bold mb-1 d-block">Quantity</label>
                <input id="pd-qty" v-model.number="quantity" type="number" class="form-control" style="width: 110px;"
                  :min="product.min_order_quantity" :max="product.quantity_available" />
              </div>
              <button type="button" class="btn mk-btn flex-fill" :disabled="!canAdd" @click="addToCart">
                <i class="fe fe-shopping-cart mr-1"></i>Add to cart · {{ money(quantity * product.unit_price) }}
              </button>
            </div>
            <div class="small text-danger mt-2" v-if="quantityError">{{ quantityError }}</div>

            <div v-if="product.description" class="mt-4">
              <h6 class="font-weight-bold">Description</h6>
              <p class="mk-desc">{{ product.description }}</p>
            </div>
          </div>

          <div class="mk-panel mt-3" v-if="product.vendor">
            <div class="d-flex align-items-center" style="gap: 12px;">
              <img v-if="product.vendor.logo_url" :src="product.vendor.logo_url" :alt="product.vendor.name" class="mk-vlogo" />
              <div>
                <div class="small text-muted">Sold by</div>
                <router-link :to="{ path: '/marketplace', query: { vendor: product.vendor.id } }" class="font-weight-bold">
                  {{ product.vendor.name }}
                </router-link>
                <div class="small text-muted" v-if="product.vendor.state"><i class="fe fe-map-pin mr-1"></i>{{ product.vendor.state }}</div>
              </div>
            </div>
            <p class="small text-muted mt-3 mb-0">
              Orders go to the vendor for confirmation. Payment is arranged directly with the vendor and recorded on the order.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import MarketplaceHeader from "./MarketplaceHeader.vue";
import cart from "./cart";

export default {
  components: { MarketplaceHeader },
  data() {
    return { product: null, loading: true, active: 0, quantity: 1 };
  },
  computed: {
    images() {
      return (this.product && this.product.image_urls) || [];
    },
    quantityError() {
      if (!this.product) return "";
      if (this.quantity < this.product.min_order_quantity) return `The minimum order is ${this.product.min_order_quantity}.`;
      if (this.quantity > this.product.quantity_available) return `Only ${this.product.quantity_available} in stock.`;
      return "";
    },
    canAdd() {
      return this.product && this.quantity > 0 && !this.quantityError;
    },
  },
  created() {
    const params = this.$route.query.agency ? { agency_id: this.$route.query.agency } : {};
    this.axios
      .get(`/api/v1/marketplace/products/${this.$route.params.id}`, { params })
      .then((res) => {
        this.product = res.data.data;
        this.quantity = this.product.min_order_quantity || 1;
      })
      .catch(() => (this.product = null))
      .finally(() => (this.loading = false));
  },
  methods: {
    money(v) {
      return "₦" + Number(v || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });
    },
    addToCart() {
      cart.add(this.product, this.quantity);
      this.$toasted.success("Added to cart", {
        position: "top-center",
        duration: 3000,
        action: { text: "View cart", onClick: () => this.$router.push("/marketplace/cart") },
      });
    },
  },
};
</script>

<style scoped>
.mk-page { background: #f1f5f9; min-height: 100vh; }
.mk-panel { background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 1px 4px rgba(0,0,0,.05); }
.mk-gallery { background: #fff; border-radius: 12px; height: 380px; display: flex; align-items: center; justify-content: center; color: #cbd5e1; font-size: 4rem; overflow: hidden; }
.mk-gallery img { width: 100%; height: 100%; object-fit: contain; }
.mk-thumb { width: 64px; height: 64px; border: 2px solid transparent; border-radius: 8px; padding: 0; overflow: hidden; background: #fff; }
.mk-thumb.active { border-color: #2563eb; }
.mk-thumb img { width: 100%; height: 100%; object-fit: cover; }
.mk-price { font-size: 1.6rem; font-weight: 700; color: #1e3a5f; }
.mk-price small { font-size: .85rem; font-weight: 400; color: #64748b; }
.mk-btn { background: #1e3a5f; color: #fff; border-radius: 8px; font-weight: 600; }
.mk-btn:hover { opacity: .9; color: #fff; }
.mk-btn:disabled { opacity: .5; }
.mk-desc { white-space: pre-line; color: #334155; font-size: .92rem; }
.mk-vlogo { width: 48px; height: 48px; border-radius: 10px; object-fit: cover; }
</style>
